import React from 'react'
import PropTypes from 'prop-types'
import Turbolinks from 'turbolinks'
import { urlHelpers } from '../../helpers'

/* doc
---
title: Link
name: Link
category: React - Core
---

A `Link` component is a wrapper for an `<a>` element.

### Props
| Prop Name       | Description                                 |Type            | Default Value             | Required?     |
|-----------------|---------------------------------------------|----------------|---------------------------|---------------|
| href            | destination                                 | string         |                           | yes           |
| linkRef         | ref to be added to link                     | string         |                           | no            |
| target          | how link should be opened                   | string         | _self                     | no            |
| trackEvent      | If click event should be tracked.             | boolean        | false                     | no            |
| eventCategory   | GA event category.                            | string         |                           | no            |
| eventAction     | GA event action.                              | string         |                           | no            |
| eventLabel      | GA event label.                               | string         |                           | no            |
| eventValue      | GA event value.                               | number         |                           | no            |

```html_example
  <Link href="#" target="_blank" ref="this-cool-link">
    ...
  </Link>
```
*/
function Link(props) {
  const {
    children, href, linkRef, target, trackEvent, eventCategory, eventAction, eventLabel, eventValue, onClick, ...rest
  } = props

  const linkHref = urlHelpers.addRef(href, linkRef)

  /**
  * Wrapper for onClick. If event tracking is on, then the event is tracked before
  * changing the url.
  */
  const onClickHandler = (e) => {
    if (trackEvent) {
      e.preventDefault()
      $.track('event', eventCategory, eventAction, eventLabel, eventValue, true, () => {

        // If there's an onClick callback provided, we'll just skip handling the link visit
        // all together and let the implementor worry about that. Otherwise we'll continue on
        // with the defaut link behavior.
        if (typeof onClick === 'function') {
          onClick(e)
        } else {
          Turbolinks.visit(linkHref)
        }
      })
    } else if (typeof onClick === 'function') {
      onClick(e)
    }
  }

  return (
    <a
      onClick={onClickHandler}
      {...rest}
      href={linkHref}
      target={target}
      rel={target === '_blank' ? 'noopener noreferrer' : null}
    >
      {children}
    </a>
  )
}

Link.propTypes = {
  // Link destination.
  href: PropTypes.string.isRequired,
  // Ref value to be added to href.
  linkRef: PropTypes.string,
  // Link target
  target: PropTypes.string,
  // If the event should be recorded with GA.
  trackEvent: PropTypes.bool,
  // Category for event tracking.
  eventCategory: PropTypes.string,
  // Action for event tracking.
  eventAction: PropTypes.string,
  // Label for event tracking.
  eventLabel: PropTypes.string,
  // Value for event tracking.
  eventValue: PropTypes.number,
}

export default Link
